import React, { useState } from "react";
import axios from "axios";
import { FaTrash, FaFilePdf, FaCloudUploadAlt } from "react-icons/fa";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import "./uploadMedia.css";
 
const UploadMedia = ({
  images, setImages,
  videos, setVideos,
  attachments, setAttachments,
  label = "Upload Media (Images, Videos, PDFs)"
}) => {
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken;

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit

  // Handle file selection with 5 MB limit
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    const oversizedFiles = [];

    files.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
        oversizedFiles.push(`${file.name} (${sizeMb} MB)`);
      } else {
        validFiles.push(file);
      }
    });

    if (oversizedFiles.length > 0) {
      alert(
        `The following file(s) exceed the maximum allowed limit of 5 MB:\n\n` +
          oversizedFiles.map((f) => `• ${f}`).join("\n") +
          `\n\nPlease select files smaller than 5 MB.`
      );
    }

    if (validFiles.length > 0) {
      setPendingFiles((prev) => [...prev, ...validFiles]);
    }
    e.target.value = null;
  };

  // Remove a pending file before upload
  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove an uploaded file
  const removeUploadedFile = (type, index) => {
    if (type === "image") setImages(images.filter((_, i) => i !== index));
    if (type === "video") setVideos(videos.filter((_, i) => i !== index));
    if (type === "pdf") setAttachments(attachments.filter((_, i) => i !== index));
  };

  // Upload pending files
const uploadFiles = async () => {
  if (pendingFiles.length === 0) return;
  setUploading(true);

  const mediaToUpload = pendingFiles.filter((f) =>
    f.type.startsWith("image/") || f.type.startsWith("video/")
  );
  const pdfsToUpload = pendingFiles.filter((f) => f.type === "application/pdf");

  try {
    // Images & Videos
    if (mediaToUpload.length > 0) {
      const formData = new FormData();
      mediaToUpload.forEach(file => formData.append("files", file));
      const res = await axios.post(
        `${baseUrl}/all/upload/files/images`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const uploaded = res.data.propertyImages || [];
      console.log(uploaded);
      const uploadedImages = uploaded.filter(m => !m.imagePath.endsWith(".mp4"));
      const uploadedVideos = uploaded.filter(m => m.imagePath.endsWith(".mp4"));
      setImages(prev => [...prev, ...uploadedImages]);
      setVideos(prev => [...prev, ...uploadedVideos]);
    }

    // PDFs
    if (pdfsToUpload.length > 0) {
      const formData = new FormData();
      pdfsToUpload.forEach(file => formData.append("files", file));
      const res = await axios.post(
        `${baseUrl}/all/upload/files/attachments`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const uploadedPdfs = res.data.propertyAttachments || [];
      setAttachments(prev => [...prev, ...uploadedPdfs]);
    }

    setPendingFiles([]);
  } catch (err) {
    alert("Upload failed!");
  } finally {
    setUploading(false);
  }
};

  return (

    <div className="custom-uploader">
      <label className="form-label fw-semibold mb-3">{label}</label>

      {/* Drop zone */}
      <div
        className="upload-dropzone mb-4"
        onClick={() => document.getElementById("fileInput").click()}
      >
        <FaCloudUploadAlt size={40} className="text-primary mb-2" />
        <p className="mb-0 fw-medium">Click or drag files here to upload</p>
        <small className="text-muted">Accepted: images, videos, PDFs</small>
        <input
          id="fileInput"
          type="file"
          accept="image/*,video/*,application/pdf"
          multiple
          className="d-none"
          onChange={handleFileChange}
        />
      </div>

      {/* Pending files */}
      {pendingFiles.length > 0 && (
        <div className="mb-4">
          <h6 className="fw-semibold mb-3">Files to Upload</h6>
          <div className="row g-3">
            {pendingFiles.map((file, idx) => (
              <div className="col-6 col-sm-4 col-md-3" key={idx}>
                <div className="media-card position-relative">
                  <button
                    type="button"
                    className="delete-icon"
                    onClick={() => removePendingFile(idx)}
                    title="Remove file"
                  >
                    <FaTrash className="text-danger" />
                  </button>
                  {file.type.startsWith("image/") ? (
                    <img src={URL.createObjectURL(file)} className="media-thumb" alt="" />
                  ) : file.type.startsWith("video/") ? (
                    <video src={URL.createObjectURL(file)} className="media-thumb" controls />
                  ) : (
                    <div className="media-thumb d-flex flex-column justify-content-center align-items-center bg-light text-danger">
                      <FaFilePdf size={32} />
                      <p className="small mt-2">PDF</p>
                    </div>
                  )}
                </div>
                <p className="small mt-1 text-center text-truncate">{file.name}</p>
              </div>
            ))}
          </div>
          <div className="text-end mt-3">
            {/* <button className="btn btn-info" onClick={uploadFiles} disabled={uploading}>
              {uploading ? "Uploading..." : "Upload Files"}
            </button> */}
            <button
              className="btn-glass-upload"
              onClick={uploadFiles}
              disabled={uploading}
            >
              <FaCloudUploadAlt className="me-2" />
              {uploading ? "Uploading..." : "Upload Files"}
            </button>
          </div>
        </div>
      )}

      {/* Uploaded files */}
      {(images.length > 0 || videos.length > 0 || attachments.length > 0) && (
        <div>
          <h6 className="fw-semibold mb-3">Uploaded Files</h6>
          <div className="row g-3">
            {[...images, ...videos, ...attachments].map((file, idx) => {
              const url = file.image_url;
              const isImage = /\.(jpeg|jpg|png|gif)$/i.test(url);
              const isVideo = /\.(mp4|webm|ogg)$/i.test(url);

              return (
                <div className="col-6 col-sm-4 col-md-3" key={idx}>
                  <div className="media-card position-relative">
                    <button
                      type="button"
                      className="delete-icon"
                      title="Remove file"
                      onClick={() =>
                        isImage
                          ? removeUploadedFile("image", idx)
                          : isVideo
                          ? removeUploadedFile("video", idx)
                          : removeUploadedFile("pdf", idx)
                      }
                    >
                      <FaTrash className="text-danger" />
                    </button>
                    {isImage ? (
                      <img src={url} className="media-thumb" alt="" />
                    ) : isVideo ? (
                      <video src={url} className="media-thumb" controls />
                    ) : (
                      <div className="media-thumb d-flex flex-column justify-content-center align-items-center bg-light text-danger">
                        <FaFilePdf size={32} />
                        <p className="small mt-2">PDF</p>
                      </div>
                    )}
                  </div>
                  <p className="small mt-1 text-center text-truncate">{file.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>


  );
};

UploadMedia.propTypes = {
  images: PropTypes.array.isRequired,
  setImages: PropTypes.func.isRequired,
  videos: PropTypes.array.isRequired,
  setVideos: PropTypes.func.isRequired,
  attachments: PropTypes.array.isRequired,
  setAttachments: PropTypes.func.isRequired,
  label: PropTypes.string
};

export default UploadMedia;